<template>
	<div v-if="isOpen" class="course-choice-modal-backdrop" role="presentation">
		<div
			class="course-choice-modal"
			role="dialog"
			aria-modal="true"
			aria-labelledby="course-choice-modal-title"
			@click.stop
		>
			<button
				class="course-choice-modal__close"
				type="button"
				aria-label="Закрыть окно выбора курса"
				@click="openCloseConfirm"
			>
				&times;
			</button>

			<h2 id="course-choice-modal-title" class="course-choice-modal__title">
				Добро пожаловать в МеталлТрейд Академию, {{ userFirstName }}!
			</h2>
			<p class="course-choice-modal__text">
				Вы пока не зарегистрированы ни на один курс.
			</p>
			<p class="course-choice-modal__text">
				Предлагаем выбрать курс от наших лучших авторов.
			</p>

			<div class="course-choice-modal__filters">
				<input
					v-model="searchQueryLocal"
					class="course-choice-modal__search"
					type="text"
					placeholder="Поиск курса"
				/>
				<select class="course-choice-modal__select" v-model="selectedTeacherKeyLocal">
					<option value="">Все преподаватели</option>
					<option
						v-for="teacher in teacherOptionsLocal"
						:key="teacher.key"
						:value="teacher.key"
					>
						{{ teacher.label }}
					</option>
				</select>
			</div>

			<div class="course-choice-modal__slider">
				<button
					class="course-choice-modal__nav-btn"
					type="button"
					:disabled="sliderPage <= 0"
					@click="prevPage"
					aria-label="Предыдущие популярные курсы"
				>
					‹
				</button>

				<div class="course-choice-modal__window">
					<div class="course-choice-modal__track" :style="sliderTrackStyle">
						<div
							v-for="(pageCourses, pageIndex) in sliderPages"
							:key="`slider-page-${pageIndex}`"
							class="course-choice-modal__slide"
							:style="slideStyle"
						>
							<button
								v-for="course in pageCourses"
								:key="course.id"
								type="button"
								class="course-choice-modal__card"
								@click="$emit('go-to-course', course.id)"
							>
								<div class="course-choice-modal__card-image-wrapper">
									<img
										v-if="getCourseImage(course)"
										:src="getCourseImage(course)"
										:alt="course.title"
										class="course-choice-modal__card-image"
									/>
									<div
										v-else
										class="course-choice-modal__card-image-placeholder"
										aria-hidden="true"
									>
										<svg viewBox="0 0 24 24" width="36" height="36">
											<rect
												x="3"
												y="3"
												width="18"
												height="18"
												rx="6"
												ry="6"
												fill="#e3f2ff"
											/>
											<circle
												cx="12"
												cy="12"
												r="5"
												stroke="#0095ec"
												stroke-width="2"
												fill="none"
											/>
										</svg>
									</div>
								</div>

								<div class="course-choice-modal__card-content">
									<span class="course-choice-modal__card-title">
										{{ course.title }}
									</span>
									<span class="course-choice-modal__card-description">
										{{ getCourseDescription(course) }}
									</span>

									<div class="course-choice-modal__card-teacher">
										<div class="course-choice-modal__card-teacher-avatar">
											<img
												v-if="getTeacherAvatar(course)"
												:src="getTeacherAvatar(course)"
												:alt="course.teacher_name || 'Автор курса'"
												class="course-choice-modal__card-teacher-avatar-img"
											/>
											<span v-else>
												{{ getTeacherInitials(course.teacher_name) }}
											</span>
										</div>
										<div class="course-choice-modal__card-teacher-info">
											<span class="course-choice-modal__card-teacher-label">
												Автор
											</span>
											<span class="course-choice-modal__card-teacher-name">
												{{ course.teacher_name || 'Не указан' }}
											</span>
										</div>
									</div>
								</div>
							</button>
						</div>
					</div>

					<p v-if="!filteredCoursesLocal.length" class="course-choice-modal__empty">
						Курсы по заданным параметрам пока не найдены.
					</p>
				</div>

				<button
					class="course-choice-modal__nav-btn"
					type="button"
					:disabled="sliderPage >= totalPages - 1 || totalPages <= 1"
					@click="nextPage"
					aria-label="Следующие популярные курсы"
				>
					›
				</button>
			</div>

			<div class="course-choice-modal__meta">
				{{ totalPages ? sliderPage + 1 : 1 }} / {{ Math.max(totalPages, 1) }}
			</div>

			<div class="course-choice-modal__dots" v-if="totalPages > 1">
				<button
					v-for="idx in totalPages"
					:key="`dot-${idx}`"
					type="button"
					class="course-choice-modal__dot"
					:class="{ 'course-choice-modal__dot--active': sliderPage === idx - 1 }"
					@click="goToPage(idx - 1)"
					:aria-label="`Страница ${idx}`"
				></button>
			</div>

			<button
				class="course-choice-modal__catalog-btn"
				type="button"
				@click="emitCatalog"
			>
				Перейти к каталогу
			</button>
		</div>
	</div>

	<div v-if="showCloseConfirm" class="course-choice-confirm-backdrop" role="presentation">
		<div class="course-choice-confirm" role="dialog" aria-modal="true" @click.stop>
			<p class="course-choice-confirm__text">
				Вы закрываете окно выбора курса.
			</p>
			<p class="course-choice-confirm__text">
				Если позже решите записаться, воспользуйтесь поиском или
				рекомендациями ниже на странице.
			</p>
			<div class="course-choice-confirm__actions">
				<button
					type="button"
					class="course-choice-confirm__btn course-choice-confirm__btn--primary"
					@click="confirmDismiss"
				>
					Продолжить
				</button>
				<button
					type="button"
					class="course-choice-confirm__btn course-choice-confirm__btn--secondary"
					@click="showCloseConfirm = false"
				>
					Остаться
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import { DEMO_TEACHERS } from '@/utils/courseUtils'

export default {
	name: 'StudentCourseChoiceModal',
	props: {
		isOpen: {
			type: Boolean,
			default: false,
		},
		userFirstName: {
			type: String,
			default: 'друг',
		},
		courses: {
			type: Array,
			default: () => [],
		},
	},
	emits: ['dismiss', 'go-to-course', 'go-to-catalog'],
	data() {
		return {
			searchQueryLocal: '',
			selectedTeacherKeyLocal: '',
			showCloseConfirm: false,
			sliderPage: 0,
			windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1920,
			preventEscapeHandler: null,
			resizeHandler: null,
		}
	},
	computed: {
		cardsPerPage() {
			return this.windowWidth <= 768 ? 1 : 3
		},
		filteredCoursesLocal() {
			const query = this.searchQueryLocal.trim().toLowerCase()
			return (this.courses || []).filter(course => {
				const matchesTeacher =
					!this.selectedTeacherKeyLocal ||
					course?.teacher_email === this.selectedTeacherKeyLocal ||
					course?.teacher_name === this.selectedTeacherKeyLocal
				if (!matchesTeacher) return false

				if (!query) return true
				const title = String(course?.title || '').toLowerCase()
				const description = String(course?.description || '').toLowerCase()
				const teacher = String(course?.teacher_name || '').toLowerCase()
				return (
					title.includes(query) ||
					description.includes(query) ||
					teacher.includes(query)
				)
			})
		},
		teacherOptionsLocal() {
			const seen = new Map()
			;(this.courses || []).forEach(course => {
				const email = course?.teacher_email || ''
				const name = course?.teacher_name || ''
				const key = email || name
				const label = name && email ? `${name} (${email})` : name || email
				if (!key || seen.has(key)) return
				seen.set(key, { key, label })
			})
			return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label))
		},
		sliderPages() {
			const result = []
			const source = this.filteredCoursesLocal
			for (let i = 0; i < source.length; i += this.cardsPerPage) {
				result.push(source.slice(i, i + this.cardsPerPage))
			}
			return result.length ? result : [[]]
		},
		totalPages() {
			return this.sliderPages.length
		},
		sliderTrackStyle() {
			return {
				width: `${this.totalPages * 100}%`,
				transform: `translateX(-${(this.sliderPage * 100) / this.totalPages}%)`,
			}
		},
		slideStyle() {
			return {
				width: `${100 / this.totalPages}%`,
			}
		},
	},
	watch: {
		searchQueryLocal() {
			this.sliderPage = 0
		},
		selectedTeacherKeyLocal() {
			this.sliderPage = 0
		},
		totalPages(newTotal) {
			if (this.sliderPage > newTotal - 1) {
				this.sliderPage = Math.max(0, newTotal - 1)
			}
		},
		isOpen(value) {
			if (value) {
				this.attachGlobalHandlers()
				return
			}
			this.detachGlobalHandlers()
			this.showCloseConfirm = false
		},
	},
	mounted() {
		if (this.isOpen) {
			this.attachGlobalHandlers()
		}
	},
	beforeUnmount() {
		this.detachGlobalHandlers()
	},
	methods: {
		openCloseConfirm() {
			this.showCloseConfirm = true
		},
		confirmDismiss() {
			this.showCloseConfirm = false
			this.$emit('dismiss')
		},
		prevPage() {
			if (this.sliderPage > 0) this.sliderPage -= 1
		},
		nextPage() {
			if (this.sliderPage < this.totalPages - 1) this.sliderPage += 1
		},
		goToPage(page) {
			if (page < 0 || page > this.totalPages - 1) return
			this.sliderPage = page
		},
		emitCatalog() {
			this.$emit('go-to-catalog', this.searchQueryLocal.trim())
		},
		getCourseImage(course) {
			if (!course || typeof course !== 'object') return ''
			return (
				course.cover_image_url ||
				course.coverImage ||
				course.cover ||
				course.image ||
				''
			)
		},
		getCourseDescription(course) {
			const fallback = 'Описание отсутствует'
			if (!course || typeof course !== 'object') return fallback
			const value = course.description || course.short_description || ''
			if (!value || typeof value !== 'string') return fallback
			return value.trim() || fallback
		},
		getTeacherInitials(name) {
			if (!name || typeof name !== 'string') return 'NA'
			const parts = name
				.trim()
				.split(/\s+/)
				.filter(Boolean)
				.slice(0, 2)
			if (!parts.length) return 'NA'
			return parts.map(part => part[0]).join('').toUpperCase()
		},
		getTeacherAvatar(course) {
			if (!course || typeof course !== 'object') return ''
			const directAvatar =
				course.teacher_avatar ||
				course.teacher_avatar_url ||
				course.teacherAvatar ||
				course.author_avatar ||
				course.author_avatar_url ||
				''
			if (directAvatar) return directAvatar
			const demoTeacher = DEMO_TEACHERS[course.teacher_name?.trim?.() || '']
			return demoTeacher?.avatar || ''
		},
		attachGlobalHandlers() {
			if (typeof window === 'undefined') return
			this.preventEscapeHandler = event => {
				if (!this.isOpen && !this.showCloseConfirm) return
				if (event.key === 'Escape') {
					event.preventDefault()
					event.stopPropagation()
				}
			}
			this.resizeHandler = () => {
				this.windowWidth = window.innerWidth
			}
			window.addEventListener('keydown', this.preventEscapeHandler, true)
			window.addEventListener('resize', this.resizeHandler)
		},
		detachGlobalHandlers() {
			if (typeof window === 'undefined') return
			if (this.preventEscapeHandler) {
				window.removeEventListener('keydown', this.preventEscapeHandler, true)
				this.preventEscapeHandler = null
			}
			if (this.resizeHandler) {
				window.removeEventListener('resize', this.resizeHandler)
				this.resizeHandler = null
			}
		},
	},
}
</script>

<style scoped>
.course-choice-modal-backdrop,
.course-choice-confirm-backdrop {
	position: fixed;
	inset: 0;
	background-color: rgba(15, 23, 42, 0.58);
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	z-index: 1000000000002;
}

.course-choice-confirm-backdrop {
	z-index: 1000000000003;
}

.course-choice-modal {
	position: relative;
	width: min(980px, 100%);
	background: #ffffff;
	border-radius: 18px;
	padding: 26px 26px 22px;
	box-shadow: 0 22px 44px rgba(15, 23, 42, 0.22);
}

.course-choice-modal__close {
	position: absolute;
	top: 14px;
	right: 14px;
	width: 36px;
	height: 36px;
	border: none;
	border-radius: 50%;
	background: #f1f5f9;
	color: #0f172a;
	font-size: 26px;
	line-height: 1;
	cursor: pointer;
}

.course-choice-modal__close:hover {
	background: #e2e8f0;
}

.course-choice-modal__title {
	font-size: 28px;
	font-weight: 700;
	line-height: 1.3;
	margin: 0 52px 10px 0;
	color: #0f172a;
	text-align: center;
}

.course-choice-modal__text {
	margin: 0;
	font-size: 17px;
	line-height: 1.5;
	color: #334155;
	text-align: center;
}

.course-choice-modal__text + .course-choice-modal__text {
	margin-top: 4px;
}

.course-choice-modal__filters {
	margin-top: 18px;
	display: grid;
	grid-template-columns: 1fr 260px;
	gap: 12px;
}

.course-choice-modal__search,
.course-choice-modal__select {
	height: 46px;
	border: 1px solid #cbd5e1;
	border-radius: 10px;
	padding: 0 14px;
	font-size: 15px;
	color: #0f172a;
	background: #ffffff;
}

.course-choice-modal__search:focus,
.course-choice-modal__select:focus {
	outline: none;
	border-color: #2563eb;
	box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.2);
}

.course-choice-modal__slider {
	margin-top: 18px;
	display: grid;
	grid-template-columns: 46px 1fr 46px;
	gap: 12px;
	align-items: stretch;
}

.course-choice-modal__window {
	position: relative;
	overflow-x: hidden;
	overflow-y: visible;
	padding: 8px 10px 16px;
	margin: -8px -10px -16px;
	min-height: 310px;
}

.course-choice-modal__track {
	display: flex;
	transition: transform 0.45s cubic-bezier(0.22, 0.61, 0.36, 1);
	will-change: transform;
}

.course-choice-modal__slide {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 12px;
	min-height: 310px;
}

.course-choice-modal__nav-btn {
	border: 1px solid #cbd5e1;
	border-radius: 10px;
	background: #ffffff;
	font-size: 24px;
	color: #0f172a;
	cursor: pointer;
}

.course-choice-modal__nav-btn:disabled {
	opacity: 0.45;
	cursor: not-allowed;
}

.course-choice-modal__card {
	text-align: left;
	border: 1px solid #dbeafe;
	border-radius: 16px;
	padding: 11px;
	background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
	cursor: pointer;
	display: flex;
	flex-direction: column;
	gap: 10px;
	min-height: 300px;
	transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
}

.course-choice-modal__card:hover {
	transform: translateY(-6px);
	box-shadow: 0 14px 24px rgba(0, 149, 236, 0.2);
	border-color: #93c5fd;
}

.course-choice-modal__card-image-wrapper {
	width: 100%;
	flex-shrink: 0;
}

.course-choice-modal__card-image,
.course-choice-modal__card-image-placeholder {
	width: 100%;
	height: 138px;
	border-radius: 10px;
}

.course-choice-modal__card-image {
	display: block;
	object-fit: cover;
}

.course-choice-modal__card-image-placeholder {
	display: flex;
	align-items: center;
	justify-content: center;
	background: linear-gradient(135deg, #f0f7ff 0%, #e6f0ff 100%);
}

.course-choice-modal__card-content {
	display: flex;
	flex-direction: column;
	flex: 1;
	gap: 10px;
}

.course-choice-modal__card-title {
	font-size: 14px;
	font-weight: 700;
	color: #1a202c;
	line-height: 1.35;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

.course-choice-modal__card-description {
	font-size: 12px;
	line-height: 1.45;
	color: #64748b;
	display: -webkit-box;
	-webkit-line-clamp: 3;
	-webkit-box-orient: vertical;
	overflow: hidden;
	min-height: 52px;
}

.course-choice-modal__card-teacher {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: auto;
	padding-top: 8px;
	border-top: 1px solid #e2e8f0;
}

.course-choice-modal__card-teacher-avatar {
	width: 28px;
	height: 28px;
	border-radius: 50%;
	background: #0095ec;
	color: #ffffff;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 11px;
	font-weight: 700;
	flex-shrink: 0;
	overflow: hidden;
}

.course-choice-modal__card-teacher-avatar-img {
	width: 100%;
	height: 100%;
	display: block;
	object-fit: cover;
}

.course-choice-modal__card-teacher-info {
	display: flex;
	flex-direction: column;
	min-width: 0;
}

.course-choice-modal__card-teacher-label {
	font-size: 10px;
	line-height: 1.2;
	color: #94a3b8;
}

.course-choice-modal__card-teacher-name {
	font-size: 12px;
	font-weight: 600;
	color: #1a202c;
	line-height: 1.3;
	white-space: nowrap;
	text-overflow: ellipsis;
	overflow: hidden;
}

.course-choice-modal__empty {
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 0;
	padding: 14px;
	border: 1px dashed #cbd5e1;
	border-radius: 12px;
	color: #475569;
	font-size: 14px;
	background: #ffffff;
}

.course-choice-modal__meta {
	margin-top: 12px;
	text-align: center;
	font-size: 13px;
	font-weight: 500;
	color: #64748b;
}

.course-choice-modal__dots {
	margin-top: 8px;
	display: flex;
	justify-content: center;
	gap: 8px;
}

.course-choice-modal__dot {
	width: 8px;
	height: 8px;
	border: none;
	border-radius: 50%;
	background: #cbd5e1;
	cursor: pointer;
}

.course-choice-modal__dot--active {
	width: 22px;
	border-radius: 999px;
	background: #2563eb;
}

.course-choice-modal__catalog-btn {
	margin-top: 14px;
	width: 100%;
	height: 46px;
	border: none;
	border-radius: 10px;
	background: #1d4ed8;
	color: #ffffff;
	font-size: 15px;
	font-weight: 600;
	cursor: pointer;
}

.course-choice-modal__catalog-btn:hover {
	background: #1e40af;
}

.course-choice-confirm {
	width: min(560px, 100%);
	background: #ffffff;
	border-radius: 16px;
	padding: 24px;
	box-shadow: 0 22px 44px rgba(15, 23, 42, 0.24);
}

.course-choice-confirm__text {
	margin: 0;
	font-size: 16px;
	line-height: 1.5;
	color: #1e293b;
}

.course-choice-confirm__text + .course-choice-confirm__text {
	margin-top: 8px;
}

.course-choice-confirm__actions {
	margin-top: 18px;
	display: flex;
	justify-content: flex-end;
	gap: 10px;
}

.course-choice-confirm__btn {
	height: 40px;
	padding: 0 16px;
	border-radius: 9px;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
}

.course-choice-confirm__btn--primary {
	border: none;
	background: #dc2626;
	color: #ffffff;
}

.course-choice-confirm__btn--secondary {
	border: 1px solid #cbd5e1;
	background: #ffffff;
	color: #0f172a;
}

@media (max-width: 768px) {
	.course-choice-modal {
		padding: 20px 16px 16px;
	}

	.course-choice-modal__title {
		font-size: 22px;
		margin-right: 40px;
	}

	.course-choice-modal__filters {
		grid-template-columns: 1fr;
	}

	.course-choice-modal__slider {
		grid-template-columns: 1fr;
	}

	.course-choice-modal__window {
		min-height: 250px;
	}

	.course-choice-modal__slide {
		grid-template-columns: 1fr;
		min-height: 250px;
	}

	.course-choice-modal__card {
		min-height: 250px;
	}

	.course-choice-modal__card-image,
	.course-choice-modal__card-image-placeholder {
		height: 126px;
	}

	.course-choice-modal__nav-btn {
		height: 42px;
	}

	.course-choice-confirm {
		padding: 18px 14px;
	}

	.course-choice-confirm__actions {
		flex-direction: column;
	}

	.course-choice-confirm__btn {
		width: 100%;
	}
}
</style>
